import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Checkbox,
  DialogTitle,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import {
  INIT_PR_INVESTIGATION_FORM_VALUE,
  PRInvestigationSchema,
} from "../../schema/prInvestigation";
import Loader from "../../components/loader/loader";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import {
  AppURLs,
  EFormAction,
  EOsdType,
  EPRStatus,
  ErrorMessages,
  FORM_STYLES,
  PR_INVESTIGATION_FEILDS,
} from "../../constants";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import { compareObjects } from "../../helpers";
import { IDamageInvestigation } from "../../models/prException";
import { DamageFollowupAPI } from "../../API/DamageFollowupAPI";
import TooltipWithClickAway from "../../components/terminalPicker/TooltipWithClickAway";
import NoteBox from "../../components/noteBox/NoteBox";
import PRInvestigationTemplate from "../../templates/prInvestigationTemplate";
import { useImageUploader } from "../../@hooks/useImageUploader";
import { useNavigate } from "react-router-dom";

type Props = {
  osdNumber?: number;
  osdDetails?: any;
  setInvestigationStatus: (status: number) => void;
  onInvestigationComplate: () => void;
  osdType?: string;
  isComplete?: boolean;
  onInvestigationSubmit?: () => void;
};


const DamageInvestigation: React.FC<Props> = ({
  osdNumber = 0,
  osdDetails,
  setInvestigationStatus,
  osdType,
  isComplete = false,
  onInvestigationSubmit,
  onInvestigationComplate,
}) => {
  const dispatch = useDispatch();
  const navigates = useNavigate();
  const { uploadImage } = useImageUploader();
  const isRF = osdType === EOsdType.rf;

  const investigationDetails = useMemo(() => osdDetails, [osdDetails]);
  const [loading, setLoading] = useState(false);
  const [resetTimestamps, setResetTimestamps] = useState<string>(
    Date.now().toString()
  );
  const [status, setStatus] = useState(0);
  const isfollowup = status === EPRStatus.followupInProgress;
  const [formFieldsDisabled, setFormFieldsDisabled] = useState(true);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [initValues, setInitValues] = React.useState<IDamageInvestigation>(
    INIT_PR_INVESTIGATION_FORM_VALUE
  );
  const [editFormValueChanged, setEditFormValueChanged] =
    React.useState<boolean>(false);

  const {
    errors,
    values,
    handleBlur,
    handleChange,
    resetForm,
    setFieldValue,
    validateForm,
    handleSubmit,
  } = useFormik({
    validationSchema: PRInvestigationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: initValues,
    onSubmit: (values) => {
      // Handle form submission
      setIsCompleting(true);
    },
  });

  const enableEditing = () => {
    setFormFieldsDisabled(false);
  };

  const updateInitValue = (
    key: string,
    value: string | number | null | undefined
  ) => {
    setInitValues((prevState) => {
      // Update the top-level property
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  const fieldsOnAllProductDamagedYes: (keyof IDamageInvestigation)[] = [
    "shipmentConsistsOf",
    "productDescription",
    "packageType",
    "isPackageDamaged",
    "isProductDamaged",
    "isProductNotDamaged",
    "isPackageRepairable",
    "areValidPicturesAvailable",
  ];

  const fieldsOnAllProductDamagedNo: (keyof IDamageInvestigation)[] = [
    "canProductBeRecouped",
    "willConsigneeAcceptRedelivery",
  ];

  const filterRequestParameters = (params: IDamageInvestigation) => {
    if (params.checkSpecialInstructions !== "Y") {
      delete params.specialInstructionsDetails;
    }

    if (params.willConsigneeAcceptRedelivery !== "Y") {
      delete params.consigneeName;
      delete params.consigneePhone;
      delete params.consigneeEmail;
    }
    if (params.isAllProductDamaged === "Y") {
      fieldsOnAllProductDamagedNo.forEach((key) => {
        delete params[key];
      });
    } else {
      fieldsOnAllProductDamagedYes.forEach((key) => {
        delete params[key];
      });
    }

    if (params.canProductBeRecouped !== "N") {
      delete params.reasonWhyCannotBeRecouped;
    }
  };

  // Follow-up action
  const saveOrComplete = useCallback(
    async (action = EFormAction.save) => {
      const params: IDamageInvestigation = { ...values }; // shallow clone is usually sufficient
      const errors = await validateForm();

      filterRequestParameters(params);

      if (isRF) {
        const fieldsToDelete = [
          "isAllProductDamaged",
          "shipmentConsistsOf",
          "productDescription",
          "packageType",
          "isPackageDamaged",
          "isProductDamaged",
          "isProductNotDamaged",
          "isPackageRepairable",
          "areValidPicturesAvailable",
          "canProductBeRecouped",
          "reasonWhyCannotBeRecouped",
        ];

        fieldsToDelete.forEach((key) => {
          delete errors[key as keyof IDamageInvestigation];
        });

        fieldsToDelete.forEach((key) => {
          delete params[key as keyof IDamageInvestigation];
        });
      }

      const hasValidationError = Object.keys(errors).length > 0;
      const isMissingRedelivery =
        isRF && !values?.willConsigneeAcceptRedelivery?.trim();

      const validYN = new Set(["Y", "N"]);

      (
        [
          "canProductBeRecouped",
          "willConsigneeAcceptRedelivery",
        ] as (keyof IDamageInvestigation)[]
      ).forEach((key) => {
        if (!validYN.has(params[key] as string)) {
          delete params[key];
        }
      });

      if (
        (action !== EFormAction.save ||
          status === EPRStatus.followupInProgress) &&
        (hasValidationError || isMissingRedelivery)
      ) {
        setIsCompleting(true);
        return;
      }

      setLoading(true);

      try {
        const { statusNumber } = await DamageFollowupAPI(
          params,
          osdNumber,
          action
        );
        setStatus(statusNumber);
        setIsCompleting(false);
        setInvestigationStatus(statusNumber);
        setFormFieldsDisabled(true);
        patchFormFieldValues(params);

        if (action === EFormAction.submit && onInvestigationSubmit) {
          onInvestigationSubmit();
        }

        const message =
          action === EFormAction.complete
            ? ErrorMessages.investigationNoteCompleted
            : ErrorMessages.prFollowUpSuccess;

        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.SUCCESS,
            message,
            Date.now().toString()
          )
        );
      } catch (err) {
        const errorMessage =
          typeof err === "string"
            ? err
            : "Exception follow-up failed. Please try again.";

        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.ERROR,
            errorMessage,
            Date.now().toString()
          )
        );
        console.error("Exception follow-up error:", err);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, dispatch, validateForm, osdNumber, isRF, osdType]
  );

  const resetToLastSaved = () => {
    resetForm(); // Reset to last saved values
    setResetTimestamps(Date.now().toString());
    patchFormFieldValues(initValues);
  };

  const patchFormFieldValues = (formValues: IDamageInvestigation) => {
    PR_INVESTIGATION_FEILDS.forEach((key: keyof IDamageInvestigation) => {
      const value = formValues[key] ?? undefined;
      setFieldValue(key, value, true);
      updateInitValue(key, value);
    });
  };

  const getErrorMessage = (field: keyof IDamageInvestigation) => {
    if (errors?.[field] && isCompleting) {
      return <span className="form-error">{errors?.[field]}</span>;
    }
    return null;
  };

  const resetFields = async (
    undefinedFields: (keyof IDamageInvestigation)[],
    initValueFields: (keyof IDamageInvestigation)[]
  ) => {
    await Promise.all(
      undefinedFields.map((field) => setFieldValue(field, undefined))
    );
    await Promise.all(
      initValueFields.map((field) => setFieldValue(field, initValues[field]))
    );
  };

  const sendDisposition = () => {
    if (!isfollowup) return;
    if (osdNumber) {
      navigates(
        AppURLs.sendDisposition.replace(
          ":osdNumber",
          osdNumber?.toString() ?? ""
        )
      );
      navigates(
        AppURLs.sendDisposition.replace(":osdNumber", osdNumber.toString())
      );
    } else {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          "OS&D number is not available",
          Date.now().toString()
        )
      );
    }
  };

  const willConsigneeAcceptDeliveryUI = () => {
    return (
      <Grid item md={12} xs={12}>
        <FormControl className="radio-group question-group">
          <FormLabel>
            Will the consignee accept delivery? <sup>*</sup>
          </FormLabel>
          <RadioGroup
            row
            data-testid="willConsigneeAcceptRedelivery"
            name="willConsigneeAcceptRedelivery"
            value={values?.willConsigneeAcceptRedelivery}
            onChange={handleChange}
          >
            <FormControlLabel
              value="Y"
              control={<Radio disabled={formFieldsDisabled} />}
              label="Yes"
              checked={values?.willConsigneeAcceptRedelivery === "Y"}
              onChange={(e) => {
                handleChange(e);
                setFieldValue("consigneeName", initValues.consigneeName);
                setFieldValue("consigneePhone", initValues.consigneePhone);
                setFieldValue("consigneeEmail", initValues.consigneeEmail);
              }}
              onBlur={handleBlur}
            />
            <FormControlLabel
              value="N"
              control={<Radio disabled={formFieldsDisabled} />}
              label="No"
              checked={values?.willConsigneeAcceptRedelivery === "N"}
              onChange={(e) => {
                handleChange(e);
                setFieldValue("consigneeName", undefined);
                setFieldValue("consigneePhone", undefined);
                setFieldValue("consigneeEmail", undefined);
              }}
              onBlur={handleBlur}
            />
          </RadioGroup>

          {errors?.willConsigneeAcceptRedelivery &&
          !isRF &&
          isCompleting &&
          values?.isAllProductDamaged === "N" ? (
            <span className="form-error">
              {errors?.willConsigneeAcceptRedelivery}
            </span>
          ) : null}

          {!values?.willConsigneeAcceptRedelivery?.trim() &&
            isCompleting &&
            isRF && (
              <span className="form-error">
                Required field: Please choose an option.
              </span>
            )}

          {values?.willConsigneeAcceptRedelivery === "N" && (
            <NoteBox message="Note: Must reach out to shipper for disposition." />
          )}

          {values?.willConsigneeAcceptRedelivery === "Y" && (
            <div>
              <Grid container spacing={2} className="filter-margin">
                <Grid item md={12} xs={12}>
                  <FormControl fullWidth>
                    <FormLabel>
                      Consignee Name <sup>*</sup>{" "}
                    </FormLabel>
                    <TextField
                      autoComplete="off"
                      data-testid="consigneeName"
                      name="consigneeName"
                      className="mt-1"
                      value={values?.consigneeName}
                      onChange={handleChange}
                      placeholder="Enter Consignee Name"
                      fullWidth
                      inputProps={{ maxLength: 30 }}
                      disabled={formFieldsDisabled}
                      variant="outlined"
                    />
                  </FormControl>
                  {getErrorMessage("consigneeName")}
                </Grid>

                <Grid item md={6} xs={12} className="pt-0">
                  <FormControl fullWidth>
                    <FormLabel>
                      Phone Number <sup>*</sup>
                    </FormLabel>
                    <TextField
                      autoComplete="off"
                      name="consigneePhone"
                      data-testid="consigneePhone"
                      className="mt-1"
                      value={values?.consigneePhone}
                      onChange={handleChange}
                      placeholder="Enter Phone Number"
                      fullWidth
                      inputProps={{ maxLength: 12 }}
                      disabled={formFieldsDisabled}
                      variant="outlined"
                    />
                  </FormControl>
                  {getErrorMessage("consigneePhone")}
                </Grid>

                <Grid item md={6} xs={12} className="pt-0">
                  <FormControl fullWidth>
                    <FormLabel>
                      E-mail Address <sup>*</sup>
                    </FormLabel>
                    <TextField
                      autoComplete="off"
                      name="consigneeEmail"
                      data-testid="consigneeEmail"
                      className="mt-1"
                      value={values?.consigneeEmail}
                      onChange={handleChange}
                      placeholder="Enter E-mail Address"
                      fullWidth
                      inputProps={{ maxLength: 150 }}
                      disabled={formFieldsDisabled}
                      variant="outlined"
                    />
                    {getErrorMessage("consigneeEmail")}
                  </FormControl>
                </Grid>
              </Grid>
            </div>
          )}
        </FormControl>
      </Grid>
    );
  };

  useEffect(() => {
    setEditFormValueChanged(!compareObjects(values, initValues));
    dispatch(isFormValueChanged(!compareObjects(initValues, values)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, initValues]);

  useEffect(() => {
    if (!investigationDetails) return;

    patchFormFieldValues(investigationDetails);
    if (investigationDetails?.investigationStatusNumber)
      setStatus(investigationDetails?.investigationStatusNumber);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationDetails]);

  useEffect(() => {
    if (isComplete) {
      
      onInvestigationComplate();
      setStatus(EPRStatus.needsCleared);
      const upload = async () => {
        await uploadImage({
          elementId: "damage-investigation-image-temp",
          osdNumber,
          setLoading,
        });
      };
      setTimeout(() => {
        upload();
      }, 500);
      
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  return (
    <>
      <form action="" key={resetTimestamps} onSubmit={handleSubmit}>
        <Grid
          container
          spacing={2}
          className="filter-margin accordion-container"
        >
          <Grid item md={12} xs={12}>
            <FormControl className="radio-group question-group">
              <FormLabel>
                Was the correct delivery status entered? <sup>*</sup>
              </FormLabel>
              <RadioGroup
                data-testid="wasCorrectDeliveryStatusEntered"
                row
                name="wasCorrectDeliveryStatusEntered"
              >
                <FormControlLabel
                  value="Y"
                  control={<Radio disabled={formFieldsDisabled} />}
                  label="Yes"
                  name="wasCorrectDeliveryStatusEntered"
                  data-testid="wasCorrectDeliveryStatusEntered"
                  checked={values?.wasCorrectDeliveryStatusEntered === "Y"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <FormControlLabel
                  value="N"
                  control={<Radio disabled={formFieldsDisabled} />}
                  label="No"
                  name="wasCorrectDeliveryStatusEntered"
                  data-testid="wasCorrectDeliveryStatusEntered"
                  checked={values?.wasCorrectDeliveryStatusEntered === "N"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </RadioGroup>
              {getErrorMessage("wasCorrectDeliveryStatusEntered")}

              {values?.wasCorrectDeliveryStatusEntered === "N" && (
                <NoteBox message="Note: Correct delivery status. Use option 86, from terminal operations menu, to update delivery status." />
              )}
            </FormControl>
          </Grid>

          <Grid item md={12} xs={12} className="pt-0">
            <FormControl className="radio-group question-group">
              <FormLabel>
                Confirm the delivery code matches the ADE <sup>*</sup>
                &nbsp;
                <TooltipWithClickAway
                  tooltipText="This will be populated correctly in the F7."
                  tooltipKey="tooltip2"
                />
              </FormLabel>
              <RadioGroup
                data-testid="confirmDeliveryCodeMatchesADE"
                row
                name="confirmDeliveryCodeMatchesADE"
              >
                <FormControlLabel
                  value="Y"
                  control={<Radio disabled={formFieldsDisabled} />}
                  label="Yes"
                  name="confirmDeliveryCodeMatchesADE"
                  data-testid="confirmDeliveryCodeMatchesADE"
                  checked={values?.confirmDeliveryCodeMatchesADE === "Y"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <FormControlLabel
                  value="N"
                  control={<Radio disabled={formFieldsDisabled} />}
                  label="No"
                  name="confirmDeliveryCodeMatchesADE"
                  data-testid="confirmDeliveryCodeMatchesADE"
                  checked={values?.confirmDeliveryCodeMatchesADE === "N"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                {values?.confirmDeliveryCodeMatchesADE === "N" && (
                  <div className="tooltip-custom">
                    <TooltipWithClickAway
                      tooltipText="Use option 86, from terminal operations menu, to enter a delivery status."
                      tooltipKey="tooltip3"
                    />
                  </div>
                )}
              </RadioGroup>
              {getErrorMessage("confirmDeliveryCodeMatchesADE")}
              {values?.confirmDeliveryCodeMatchesADE === "N" && (
                <NoteBox message="Note: Enter delivery status." />
              )}
            </FormControl>
          </Grid>

          <Grid item md={12} xs={12} className="pt-0">
            <FormControl className="radio-group question-group">
              <FormLabel>
                Confirm the driver took valid EP’s <sup>*</sup>
              </FormLabel>
              <RadioGroup
                row
                name="confirmDriverTookValidEPs"
                data-testid="confirmDriverTookValidEPs"
                value={values?.confirmDriverTookValidEPs}
              >
                <FormControlLabel
                  value="Y"
                  control={<Radio disabled={formFieldsDisabled} />}
                  label="Yes"
                  checked={values?.confirmDriverTookValidEPs === "Y"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <FormControlLabel
                  value="N"
                  control={<Radio disabled={formFieldsDisabled} />}
                  label="No"
                  checked={values?.confirmDriverTookValidEPs === "N"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </RadioGroup>
              {getErrorMessage("confirmDriverTookValidEPs")}
              {values?.confirmDriverTookValidEPs === "N" && (
                <NoteBox message="Note: Notify terminal manager." />
              )}
            </FormControl>
          </Grid>
          <>
            <Grid item md={12} xs={12} className="pt-0">
              <FormControl className="radio-group question-group">
                <FormLabel>
                  <FormControlLabel
                    className="checkbox-shoartage"
                    control={
                      <Checkbox
                        disableRipple
                        checked={values?.checkSpecialInstructions === "Y"}
                        disabled={formFieldsDisabled}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue(
                            "checkSpecialInstructions",
                            e.target.checked ? "Y" : "N"
                          );
                          if (e.target.checked) {
                            setFieldValue(
                              "specialInstructionsDetails",
                              initValues.specialInstructionsDetails
                            );
                          } else {
                            setFieldValue(
                              "specialInstructionsDetails",
                              undefined
                            );
                          }
                        }}
                        name="checkSpecialInstructions"
                        data-testid="checkSpecialInstructions-checkbox"
                        sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                      />
                    }
                    label=""
                  />
                  Check for special instructions on the body of bill, check the
                  BOL, and 78/23 for the shipper, consignee and 3rd party{" "}
                  <sup>*</sup>
                </FormLabel>
                {getErrorMessage("checkSpecialInstructions")}

                <Grid container spacing={2}>
                  <Grid item md={12} xs={12} className="all-short-entry">
                    {values?.checkSpecialInstructions === "Y" && (
                      <TextField
                        autoComplete="off"
                        name="specialInstructionsDetails"
                        value={values?.specialInstructionsDetails}
                        error={
                          Boolean(errors?.specialInstructionsDetails) &&
                          isCompleting
                        }
                        fullWidth
                        placeholder="Enter Special Instructions"
                        inputProps={{ maxLength: 150 }}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={formFieldsDisabled}
                        variant="outlined"
                      />
                    )}
                    {values?.checkSpecialInstructions === "Y" &&
                      getErrorMessage("specialInstructionsDetails")}
                  </Grid>
                </Grid>
              </FormControl>
            </Grid>

            <br />

            <Grid item md={12} xs={12}>
              <DialogTitle component="h5" style={{ padding: 0 }}>
                Check the verbiage in the ADE to describe the
                {isRF
                  ? ` reasoning of arbitrary refusal`
                  : ` extent of damage and non-damaged items`}
              </DialogTitle>
            </Grid>
            {isRF && willConsigneeAcceptDeliveryUI()}
            {!isRF && (
              <Grid item md={12} xs={12} className="pt-0">
                <FormControl className="radio-group question-group">
                  <FormLabel>
                    Is all of the product damaged?<sup>*</sup>
                  </FormLabel>
                  <RadioGroup
                    row
                    name="isAllProductDamaged"
                    value={values?.isAllProductDamaged}
                    onChange={handleChange}
                    data-testid="isAllProductDamaged"
                  >
                    <FormControlLabel
                      value="Y"
                      control={<Radio disabled={formFieldsDisabled} />}
                      label="Yes"
                      checked={values?.isAllProductDamaged === "Y"}
                      onChange={(e) => {
                        handleChange(e);
                        resetFields(
                          fieldsOnAllProductDamagedNo,
                          fieldsOnAllProductDamagedYes
                        );
                      }}
                      onBlur={handleBlur}
                    />
                    <FormControlLabel
                      value="N"
                      control={<Radio disabled={formFieldsDisabled} />}
                      label="No"
                      checked={values?.isAllProductDamaged === "N"}
                      onChange={(e) => {
                        handleChange(e);
                        resetFields(
                          fieldsOnAllProductDamagedYes,
                          fieldsOnAllProductDamagedNo
                        );
                      }}
                      onBlur={handleBlur}
                    />
                  </RadioGroup>
                  {getErrorMessage("isAllProductDamaged")}

                  {values?.isAllProductDamaged === "Y" && (
                    <div>
                      <Grid item md={12} xs={12}>
                        <FormControl fullWidth>
                          <FormLabel>
                            What does the shipment consist of?{" "}
                          </FormLabel>
                          <TextField
                            autoComplete="off"
                            name="shipmentConsistsOf"
                            className="mt-1"
                            value={values?.shipmentConsistsOf}
                            onChange={handleChange}
                            placeholder="Describe Steps Taken"
                            fullWidth
                            inputProps={{ maxLength: 150 }}
                            disabled={formFieldsDisabled}
                            variant="outlined"
                          />
                        </FormControl>
                      </Grid>

                      <Grid item md={12} xs={12} className="pt-0">
                        <FormControl fullWidth>
                          <FormLabel>What is the product? </FormLabel>
                          <TextField
                            autoComplete="off"
                            name="productDescription"
                            className="mt-1"
                            value={values?.productDescription}
                            onChange={handleChange}
                            placeholder="Describe Steps Taken"
                            fullWidth
                            inputProps={{ maxLength: 150 }}
                            disabled={formFieldsDisabled}
                            variant="outlined"
                          />
                        </FormControl>
                      </Grid>

                      <Grid item md={12} xs={12} className="pt-0">
                        <FormControl fullWidth>
                          <FormLabel>What kind of package? </FormLabel>
                          <TextField
                            autoComplete="off"
                            name="packageType"
                            className="mt-1"
                            value={values?.packageType}
                            onChange={handleChange}
                            placeholder="Describe Steps Taken"
                            fullWidth
                            inputProps={{ maxLength: 150 }}
                            disabled={formFieldsDisabled}
                            variant="outlined"
                          />
                        </FormControl>
                      </Grid>

                      <Grid item md={12} xs={12} className="pt-0">
                        <FormControl fullWidth>
                          <FormLabel>
                            Is there damage to the packaging?
                          </FormLabel>
                          <TextField
                            autoComplete="off"
                            name="isPackageDamaged"
                            className="mt-1"
                            value={values?.isPackageDamaged}
                            onChange={handleChange}
                            placeholder="Describe Steps Taken"
                            fullWidth
                            inputProps={{ maxLength: 150 }}
                            disabled={formFieldsDisabled}
                            variant="outlined"
                          />
                        </FormControl>
                      </Grid>

                      <Grid item md={12} xs={12} className="pt-0">
                        <FormControl fullWidth>
                          <FormLabel>
                            Is the actual product damaged, if so, how bad?
                          </FormLabel>
                          <TextField
                            autoComplete="off"
                            name="isProductDamaged"
                            className="mt-1"
                            value={values?.isProductDamaged}
                            onChange={handleChange}
                            placeholder="Describe Steps Taken"
                            fullWidth
                            inputProps={{ maxLength: 150 }}
                            disabled={formFieldsDisabled}
                            variant="outlined"
                          />
                        </FormControl>
                      </Grid>

                      <Grid item md={12} xs={12} className="pt-0">
                        <FormControl fullWidth>
                          <FormLabel>
                            Is any of the actual product NOT damaged, if so how
                            much?
                          </FormLabel>
                          <TextField
                            autoComplete="off"
                            name="isProductNotDamaged"
                            className="mt-1"
                            value={values?.isProductNotDamaged}
                            onChange={handleChange}
                            placeholder="Describe Steps Taken"
                            fullWidth
                            inputProps={{ maxLength: 150 }}
                            disabled={formFieldsDisabled}
                            variant="outlined"
                          />
                        </FormControl>
                      </Grid>

                      <Grid item md={12} xs={12} className="pt-0">
                        <FormControl fullWidth>
                          <FormLabel>Is the package repairable?</FormLabel>
                          <TextField
                            autoComplete="off"
                            name="isPackageRepairable"
                            className="mt-1"
                            value={values?.isPackageRepairable}
                            onChange={handleChange}
                            placeholder="Describe Steps Taken"
                            fullWidth
                            inputProps={{ maxLength: 150 }}
                            disabled={formFieldsDisabled}
                            variant="outlined"
                          />
                        </FormControl>
                      </Grid>

                      <Grid item md={12} xs={12} className="pt-0">
                        <FormControl fullWidth>
                          <FormLabel>
                            Are there any valid pictures, of the freight and the
                            damage, in image?
                          </FormLabel>
                          <TextField
                            autoComplete="off"
                            name="areValidPicturesAvailable"
                            className="mt-1"
                            value={values?.areValidPicturesAvailable}
                            onChange={handleChange}
                            placeholder="Describe Steps Taken"
                            fullWidth
                            inputProps={{ maxLength: 150 }}
                            disabled={formFieldsDisabled}
                            variant="outlined"
                          />
                        </FormControl>
                      </Grid>
                    </div>
                  )}

                  {values?.isAllProductDamaged === "N" && (
                    <div>
                      <Grid item md={12} xs={12}>
                        <FormControl className="radio-group question-group">
                          <FormLabel>
                            Can the product recouped? <sup>*</sup>
                          </FormLabel>
                          <RadioGroup
                            row
                            name="canProductBeRecouped"
                            value={values?.canProductBeRecouped}
                            onChange={(e) => {
                              handleChange(e);
                              setFieldValue(
                                "reasonWhyCannotBeRecouped",
                                initValues.reasonWhyCannotBeRecouped
                              );
                            }}
                            data-testid="canProductBeRecouped"
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio disabled={formFieldsDisabled} />}
                              label="Yes"
                              checked={values?.canProductBeRecouped === "Y"}
                              onChange={(e) => {
                                handleChange(e);
                                setFieldValue(
                                  "reasonWhyCannotBeRecouped",
                                  undefined
                                );
                              }}
                              onBlur={handleBlur}
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio disabled={formFieldsDisabled} />}
                              label="No"
                              checked={values?.canProductBeRecouped === "N"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </RadioGroup>
                          {values?.isAllProductDamaged === "N" &&
                            getErrorMessage("canProductBeRecouped")}
                          {values?.canProductBeRecouped === "N" && (
                            <div>
                              <Grid item md={12} xs={12}>
                                <FormControl fullWidth>
                                  <FormLabel>
                                    Provide a reason why it cannot be recouped{" "}
                                    <sup>*</sup>
                                  </FormLabel>
                                  <TextField
                                    autoComplete="off"
                                    name="reasonWhyCannotBeRecouped"
                                    className="mt-1"
                                    value={values?.reasonWhyCannotBeRecouped}
                                    onChange={handleChange}
                                    placeholder="Describe Steps Taken"
                                    fullWidth
                                    inputProps={{ maxLength: 150 }}
                                    disabled={formFieldsDisabled}
                                    variant="outlined"
                                  />
                                </FormControl>
                              </Grid>
                              {getErrorMessage("reasonWhyCannotBeRecouped")}
                            </div>
                          )}
                        </FormControl>
                      </Grid>
                      {willConsigneeAcceptDeliveryUI()}
                    </div>
                  )}
                </FormControl>
              </Grid>
            )}

            <Grid item md={12} xs={12} className="pt-0">
              <FormControl className="radio-group question-group">
                <FormLabel>
                  <FormControlLabel
                    className="checkbox-shoartage"
                    control={
                      <Checkbox
                        disableRipple
                        checked={values?.checkPhotos === "Y"}
                        disabled={formFieldsDisabled}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue(
                            "checkPhotos",
                            e.target.checked ? "Y" : "N"
                          );
                        }}
                        name="checkPhotos"
                        data-testid="checkPhotos-checkbox"
                        sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                      />
                    }
                    label=""
                  />{" "}
                  Check for photos of the product, either EP's or Photos taken
                  after the recoup <sup>*</sup>&nbsp;
                  <TooltipWithClickAway
                    tooltipText="We do not share internal pictures, only EP’s – there is a difference between sending a photo of a box with product sticking out versus the box once the product has been recouped and taped up.  "
                    tooltipKey="tooltip1"
                  />
                </FormLabel>
                {values?.checkPhotos !== "Y" && getErrorMessage("checkPhotos")}
              </FormControl>
            </Grid>

            <Grid item md={12} xs={12} className="pt-0">
              <FormControl className="radio-group question-group">
                <FormLabel>
                  <FormControlLabel
                    className="checkbox-shoartage"
                    control={
                      <Checkbox
                        disableRipple
                        checked={values?.updatedF18Notes === "Y"}
                        disabled={formFieldsDisabled}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue(
                            "updatedF18Notes",
                            e.target.checked ? "Y" : "N"
                          );
                        }}
                        name="updatedF18Notes"
                        data-testid="updatedF18Notes-checkbox"
                        sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                      />
                    }
                    label=""
                  />{" "}
                  Updated F18 notes with who you contacted (name, email and/or
                  phone number) <sup>*</sup>
                </FormLabel>
                {values?.updatedF18Notes !== "Y" &&
                  getErrorMessage("updatedF18Notes")}
              </FormControl>
            </Grid>
          </>

          <Grid item md={12} xs={12} className="pt-0">
            <FormControl fullWidth size="small">
              <InputLabel style={FORM_STYLES.LABEL} shrink={true}>
                F18 notes <sup>*</sup>
              </InputLabel>
              <TextField
                autoComplete="off"
                multiline
                rows={2}
                name="f18Note"
                data-testid="f18Note"
                value={values?.f18Note}
                onChange={handleChange}
                placeholder="Enter F18 notes "
                fullWidth
                inputProps={{ maxLength: 300 }}
                disabled={formFieldsDisabled}
                variant="outlined"
              />
            </FormControl>
            {getErrorMessage("f18Note")}
            <br />
            <br />
          </Grid>

          {/* Action Buttons */}
          {status !== EPRStatus.needsCleared && (
            <Grid item md={12} xs={12} className="btn-group-submit">
              {formFieldsDisabled ? (
                <>
                  <Button
                    variant="contained"
                    onClick={enableEditing}
                    className="assign ml mr-0"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    className={
                      isfollowup
                        ? "entry mr-0 ml"
                        : "disable-btn disable entry mr-0 ml"
                    }
                    onClick={sendDisposition}
                  >
                    Send Disposition
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    onClick={resetToLastSaved}
                    className="clear-btn mr-0 ml"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  {isfollowup ? (
                    <Button
                      variant="contained"
                      onClick={() =>
                        editFormValueChanged
                          ? saveOrComplete(EFormAction.save)
                          : null
                      }
                      className={
                        editFormValueChanged
                          ? "assign mr-0 ml"
                          : "disable-btn disable assign mr-0 ml"
                      }
                      type="submit"
                    >
                      Update
                    </Button>
                  ) : (
                    <>
                      {" "}
                      <Button
                        variant="contained"
                        onClick={() => saveOrComplete(EFormAction.save)}
                        className="entry ml"
                        type="submit"
                      >
                        Save
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => saveOrComplete(EFormAction.submit)}
                        className="entry ml"
                        type="submit"
                      >
                        Submit
                      </Button>
                    </>
                  )}
                </>
              )}
            </Grid>
          )}
        </Grid>
      </form>
      <div className="hide-me" id={`damage-investigation-image-temp`}>
        <PRInvestigationTemplate
          osdType={osdType}
          values={values}
          osdNumber={osdNumber}
        />
      </div>
      <Loader pageLoader={loading} />
    </>
  );
};

export default React.memo(DamageInvestigation);
